import { FeatureCollection } from 'geojson';
import { RawSection, Table } from '../../types/soils';
import {
  filterReportItems,
  formatSoilName,
  getMapUnitKeyFromName,
  getNameToMapUnitKeyMap,
  validateReport,
  WrappedColumn,
} from '../../utils/soils';
import { getNumericMapLegend } from '../../utils/mapLegend';

const FORESTLAND_TITLE = 'Forestland Productivity with Site Index Base';
const commonTrees = 'commonTrees';
const siteIndex = 'siteIndex';
const baseAge = 'baseAge';
const curveNumber = 'curveNumber';
const volGrowthRate = 'volGrowthRate';

const cols = [
  {
    accessor: 'name',
    displayString: 'Map unit',
    format: formatSoilName,
    style: WrappedColumn,
  },
  { accessor: commonTrees, displayString: 'Common trees' },
  { accessor: siteIndex, displayString: 'Site index (ft)' },
  { accessor: baseAge, displayString: 'Base age (yrs)' },
  { accessor: curveNumber, displayString: 'Site index curve number' },
  {
    accessor: volGrowthRate,
    displayString: 'Volume growth rate, CMAI (cu ft/ac/yr)',
  },
];

interface GroupItemsAccumulator {
  items: {
    colSpan?: { name: number };
    name: string;
    rowSpan?: { name: number };
    skipCols?: string[];
  }[];
  currentGroup: Nullable<{ name: string }>;
  groupedItems: { name: string }[];
}

const groupItems = (acc: GroupItemsAccumulator) => {
  const { items, currentGroup, groupedItems } = acc;
  const currentGroupItems = currentGroup
    ? [{ ...currentGroup, rowSpan: { name: groupedItems.length + 1 } }]
    : [];
  return [
    ...items,
    ...currentGroupItems,
    ...groupedItems.map((i) => ({ ...i, skipCols: ['name'] })),
  ];
};

const addRowSpan = (originalItems: { name: string }[]) => {
  const result = originalItems.reduce(
    (
      { items, currentGroup, groupedItems }: GroupItemsAccumulator,
      item: { name: string },
    ) => {
      if (item.name !== '') {
        return {
          items: groupItems({ items, currentGroup, groupedItems }),
          currentGroup: item,
          groupedItems: [],
        };
      }
      return { items, currentGroup, groupedItems: [...groupedItems, item] };
    },
    {
      items: [],
      currentGroup: null,
      groupedItems: [],
    },
  );
  return groupItems(result);
};

const columnSpan = (nameOnly: boolean) => ({
  colSpan: nameOnly ? { name: 6 } : undefined,
  skipCols: nameOnly
    ? [commonTrees, siteIndex, baseAge, curveNumber, volGrowthRate]
    : undefined,
});

const getItems = ({
  data,
  report: rawReport,
}: {
  data: FeatureCollection;
  report?: RawSection;
}) => {
  const report = validateReport(rawReport, FORESTLAND_TITLE);
  if (report == null) return [];
  const items = report.section.flatMap((section) =>
    section.table.flatMap((table) => [
      {
        key: table.title,
        name: table.title,
        style: { backgroundColor: '#e8e8e8' },
        ...columnSpan(true),
      },
      ...table.tbody.tr
        .slice(1)
        .map((row) => {
          return row.td.map((v) => (typeof v === 'string' ? v : v.para));
        })
        .map((td, i) => {
          const index = td.length < 6 ? -1 : 0;
          const nameOnly = td.slice(1).every((v) => v === '');
          return {
            key: `${table.title}:${i}`,
            name: index < 0 ? '' : td[0],
            [commonTrees]: td[index + 1],
            [siteIndex]: td[index + 2],
            [baseAge]: td[index + 3],
            [curveNumber]: td[index + 4],
            [volGrowthRate]: td[index + 5],
            ...columnSpan(nameOnly),
          };
        }),
    ]),
  );
  return filterReportItems(addRowSpan(items), data);
};

interface SiteIndexAccumulator {
  siteIndexMap: { [mapUnitKey: string]: Nullable<number> };
  mapUnitKey: Nullable<string>;
  siteIndices: number[];
}

const updateSiteIndexMap = (acc: SiteIndexAccumulator) => {
  const { siteIndexMap, mapUnitKey, siteIndices } = acc;
  return {
    ...siteIndexMap,
    ...(mapUnitKey
      ? { [mapUnitKey]: siteIndices.length ? Math.max(...siteIndices) : null }
      : {}),
  };
};

const reduceTableSiteIndices = (nameToKeyMap: { [key: string]: string }) => (
  result: { [key: string]: Nullable<number> },
  table: Table,
) => {
  const acc = table.tbody.tr
    .slice(1)
    .map((row) => {
      return row.td.map((v) => (typeof v === 'string' ? v : v.para));
    })
    .reduce(
      ({ siteIndexMap, mapUnitKey, siteIndices }: SiteIndexAccumulator, td) => {
        const index = td.length < 6 ? -1 : 0;
        const nameOnly = td.slice(1).every((v) => v === '');
        if (nameOnly) {
          return {
            siteIndexMap: updateSiteIndexMap({
              siteIndexMap,
              mapUnitKey,
              siteIndices,
            }),
            mapUnitKey: getMapUnitKeyFromName(td[0], nameToKeyMap),
            siteIndices: [],
          };
        }
        const siteIndexNum = parseInt(td[index + 2], 10);
        if (Number.isNaN(siteIndexNum)) {
          return { siteIndexMap, mapUnitKey, siteIndices };
        }
        return {
          siteIndexMap,
          mapUnitKey,
          siteIndices: [...siteIndices, siteIndexNum],
        };
      },
      { siteIndexMap: {}, mapUnitKey: null, siteIndices: [] },
    );
  return { ...result, ...updateSiteIndexMap(acc) };
};

const getMaximumSiteIndices = ({
  data,
  report: rawReport,
}: {
  data: FeatureCollection;
  report?: RawSection;
}): { [key: string]: Nullable<number> } => {
  const report = validateReport(rawReport, FORESTLAND_TITLE);
  if (report == null) return {};
  const nameToKeyMap = getNameToMapUnitKeyMap(data);
  return report.section.reduce(
    (result, section) => ({
      ...result,
      ...section.table.reduce(reduceTableSiteIndices(nameToKeyMap), {}),
    }),
    {},
  );
};

const getLegend = ({
  data,
  report,
}: {
  data: FeatureCollection;
  report?: RawSection;
}) => {
  const siteIndexMap = getMaximumSiteIndices({ data, report });
  return getNumericMapLegend(siteIndexMap);
};

const getColorKey = ({
  data,
  report,
}: {
  data: FeatureCollection;
  report?: RawSection;
}) => {
  const siteIndexMap = getMaximumSiteIndices({ data, report });
  const legendItems = getNumericMapLegend(siteIndexMap);
  return Object.fromEntries(
    Object.entries(siteIndexMap).flatMap(([mapUnitKey, maxSiteIndex]) => {
      const legendItem = legendItems.find(({ max }) =>
        maxSiteIndex == null ? max == null : maxSiteIndex <= (max || 0),
      );
      if (!legendItem) return [];
      return [[mapUnitKey, legendItem.hexCode]];
    }),
  );
};

export const FORESTLAND_PRODUCTIVITY = {
  key: FORESTLAND_TITLE,
  name: FORESTLAND_TITLE,
  reportId: '246',
  reportShortFormData: {
    reportid: '246',
    Includeminorsoils: 'false',
    Displaynationalmapunitsymbols: 'false',
  },
  cols,
  getColorKey,
  getItems,
  getLegend,
  units: 'maximum site index, ft',
};
